import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  Label,
  Input,
  Tooltip
} from "reactstrap";
import classnames from "classnames"; // for toggling active tab classes
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import axiosInstance from 'utils/axiosInstance';

import QuestionPane from './QuestionPane';
import ConditionsList from './ConditionsList';
import AnimatedFlowText from './AnimatedFlowText';
import AnimatedFlowTextCh from './AnimatedFlowTextCh';
import InfoQuestionLoader from '../../components/InfoQuestionLoader';

import './InfoInputAnimations.css';
import './InfoTopicInput.css';

import home4 from "assets/images/info/home4.png";
import townMap from "assets/images/info/townMap.png";

// Common Components
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
import OutlinedSvgText from "components/Common/OutlinedSvgText";



const InfoInput = ({ t }) => {

  document.title = "填寫主題 | TheChart.AI";

  const navigate = useNavigate();
  const { infoTopicId } = useParams();


  //------------------------------------------------
  // States
  //------------------------------------------------

  // Active tab
  const [activeTab, setActiveTab] = useState();
  const toggleTab = (tab) => {
    if (activeTab !== tab) { setActiveTab(tab); }
  };

  const [infoTopic, setInfoTopic] = useState(null);
  const [infoQuestionSets, setInfoQuestionSets] = useState([]);
  const [infoQuestions, setInfoQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // User inputs

  const [verifiedAnswers, setVerifiedAnswers] = useState({}); // Track verified answers
  const [verificationDetails, setVerificationDetails] = useState({});
  const [resolvedAnswerBuckets, setResolvedAnswerBuckets] = useState({});
  const [ruleVerificationStatus, setRuleVerificationStatus] = useState({}); 
  const [confirmedAnswers, setConfirmedAnswers] = useState({}); // Store confirmed inputs
  const [lastVerifiedAnswers, setLastVerifiedAnswers] = useState({}); // Store last verified answers

  const [answerAwaitVerifying, setAnswerAwaitVerifying] = useState("");
  const [answerAwaitSaving, setAnswerAwaitSaving] = useState("");


  // ---
  // ConditionsList
  // ---
  const [showActivationConditions, setShowActivationConditions] = useState(true);
  const [showAffectedAttributes, setShowAffectedAttributes] = useState(true);

  // ---
  // QuestionPane
  // ---

  // Tooltip Open States
  const [tooltipOpen, setTooltipOpen] = useState({}); // One per setIndex
  const toggleTooltip = (id) => {
    setTooltipOpen(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Stats section Open States
  const [visibleStats, setVisibleStats] = useState({});        // tracks if it should *render*
  const [animatingStats, setAnimatingStats] = useState({});    // tracks if it's *visible or hiding*

  const toggleStatsOpen = (id) => {
    setVisibleStats((prev) => {
      const nowVisible = !prev[id];

      // Show: immediately render + start visible animation
      if (nowVisible) {
        setAnimatingStats((a) => ({ ...a, [id]: true }));
        return { ...prev, [id]: true };
      }

      // Hide: start exit animation first, then unmount after delay
      setAnimatingStats((a) => ({ ...a, [id]: false }));
      setTimeout(() => {
        setVisibleStats((v) => ({ ...v, [id]: false }));
      }, 1200); // match exit animation duration

      return prev;
    });
  };




  //------------------------------------------------
  // Fetch
  //------------------------------------------------
  const [invalidTopic, setInvalidTopic] = useState(false);

  const fetchInfoData = async () => {

    try {
      const topicResponse = await axiosInstance.get(
        `/knovia/myInfo/topics/${infoTopicId}`
      );
      console.log('infoTopic Loaded:', topicResponse.data);
      setInfoTopic(topicResponse.data);
    } catch (err) {
      if (err.message === "You do not have the permission") {
        console.error("Invalid topic (401)");
        setInvalidTopic(true);
      } else {
        console.error("Error fetching topic:", err);
      }
      return; // Don't proceed if invalid
    }

    try {

      // ----
      // Load Sets in the topic
      // ----
      const questionSetsResponse = await axiosInstance.get(`/knovia/myInfo/topics/${infoTopicId}/getQuestionSets`);
      console.log('infoQuestionSets in Topic Loaded:', questionSetsResponse.data);
      // Sort by `order` before setting state
      const sortedQuestionSets = [...questionSetsResponse.data].sort((a, b) => a.order - b.order);
      setInfoQuestionSets(sortedQuestionSets);
    
      // ----
      // Load Questions in the sets
      // ----
      const questionsRes = await axiosInstance.get(`/knovia/myInfo/topics/${infoTopicId}/getQuestions`);
      console.log('infoQuestions in Topic Loaded:', questionsRes.data);
      const questions = questionsRes.data;
      // Sort by `order` before setting state
      const sortedQuestions = [...questionsRes.data].sort((a, b) => a.order - b.order);
      setInfoQuestions(sortedQuestions);
    
      // ----
      // Load Existing Answers
      // ----
      const questionIds = questions.map(q => q._id);
      const answersRes = await axiosInstance.post(
        `/knovia/myInfo/topics/${infoTopicId}/getAnswers`,
        { questionIds }
      );
      console.log('answersRes.data:', answersRes.data);
      const userAnswers = answersRes.data.answers;

      // ----
      // Initialize answers states
      // ----
      const initialAnswers = {};
      const initialVerificationStatus = {};
      const initialVerificationDetails = {};
      const initialVerifiedAnswers = {}
      const initialConfirmedAnswers = {};
      const initialResolvedAnswerBuckets = {};

      questions.forEach((q) => {
        initialAnswers[q._id] = userAnswers[q._id]?.answer || "";

        if(userAnswers[q._id]?.answer.trim().length){
          initialVerifiedAnswers[q._id] = userAnswers[q._id]?.isAIVerified || false;
          initialConfirmedAnswers[q._id] = userAnswers[q._id]?.answer || "";
          initialVerificationDetails[q._id] = userAnswers[q._id]?.aiVerificationDetails || "";
          initialResolvedAnswerBuckets[q._id] = userAnswers[q._id]?.resolvedStatsOption || "";

          // Flatten all rule sets into a per-condition status
          const passedIndexes = userAnswers[q._id]?.passedVerifyRuleIndexes || [];
          initialVerificationStatus[q._id] = [];
          if (q.verifyRuleAI) {
            q.verifyRuleAI.forEach((ruleSet, setIdx) => {
              ruleSet.conditions.forEach(() => {
                if (passedIndexes.includes(setIdx)) {
                  initialVerificationStatus[q._id].push("YES");
                } else {
                  initialVerificationStatus[q._id].push("NO");
                }
              });
            });
          }

        }

      });
      setAnswers(initialAnswers);
      setResolvedAnswerBuckets(initialResolvedAnswerBuckets);
      setVerifiedAnswers(initialVerifiedAnswers);
      setVerificationDetails(initialVerificationDetails);
      setRuleVerificationStatus(initialVerificationStatus);
      setConfirmedAnswers(initialConfirmedAnswers);



      console.log("Fetched user answers:", userAnswers);
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }



  //------------------------------------------------
  // Use Effects
  //------------------------------------------------

  const [isLoadingInfoData, setIsLoadingInfoData] = useState(true);
  // Load from server
  useEffect(() => {
    const load = async () => {
      setIsLoadingInfoData(true);
      await fetchInfoData();
      setIsLoadingInfoData(false);
    };
    load();
  }, []);

  const [animateMount, setAnimateMount] = useState(false);
  useEffect(() => {
    if (!isLoadingInfoData) {
      setTimeout(() => {
        setAnimateMount(true);
      }, 10); // short delay gives DOM time to layout before fade-in
    }
  }, [isLoadingInfoData]);

  /*
  useEffect(()=>{
    console.log('infoQuestions changed:', infoQuestions);
  },[infoQuestions])
  */

  useEffect(() => {
    if (infoQuestionSets.length > 0) {
      setActiveTab(infoQuestionSets[0]._id); // Default to first tab
    }
  }, [infoQuestionSets]);



  //------------------------------------------------
  // QuestionSet Tab Change
  //------------------------------------------------

  const [questionSetAttributes, setQuestionSetAttributes] = useState([]);
  
  // Load Attributes in Set (only for Info Admin View)
  const loadSetAttributes = async () => {
    
    const attributeResponse = await axiosInstance.get(
      `/knovia/infoQuestionSet/${activeTab}/getQuestionSetAttributes`
    );
    console.log('attributes Loaded:', attributeResponse.data);
    setQuestionSetAttributes(attributeResponse.data.questionSetAttributes);
  }
  useEffect(() => {
    if(activeTab){ loadSetAttributes(); }
  }, [activeTab]);
  


  //------------------------------------------------
  // Input Change
  //------------------------------------------------

  // Handle input change
  const handleInputChange = (questionId, event) => {
    const newAnswer = event.target.value;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: newAnswer,
    }));

    // Reset verification status and confirmed answers when input changes
    setVerifiedAnswers((prev) => ({
      ...prev,
      [questionId]: false, // Reset verification status
    }));

    setRuleVerificationStatus((prev) => ({
      ...prev,
      [questionId]: undefined, // Reset rule colors
    }));

    setConfirmedAnswers((prev) => ({
      ...prev,
      [questionId]: undefined, // Reset confirmed answers
    }));
  };


  //------------------------------------------------
  // Progress Reset
  //------------------------------------------------

  const resetTopicProgress = async () => {
    if (!infoTopicId) return;

    const confirmed = window.confirm("你確定要全部重填嗎？這會清除所有已填寫的答案。");
    if (!confirmed) return;

    try {
      await axiosInstance.post(`/knovia/myInfo/topics/${infoTopicId}/resetProgress`);
      console.log("Topic progress reset.");

      // Re-fetch everything to reflect cleared state
      fetchInfoData();
      loadSetAttributes();

    } catch (error) {
      console.error("Error resetting topic progress:", error);
      alert("重置失敗，請稍後再試。");
    }
  };

  //------------------------------------------------
  // Load Stats
  //------------------------------------------------
  const [questionStats, setQuestionStats] = useState({});
  const fetchedStatsRef = useRef(new Set());  // track fetched question IDs

  const fetchStats = async (statsIds, force = false) => {
    // Filter out already fetched IDs unless force is true
    const idsToFetch = force
      ? statsIds
      : statsIds.filter(id => !fetchedStatsRef.current.has(id));

    if (idsToFetch.length === 0) return;

    try {
      const res = await axiosInstance.get(`/knovia/myInfo/questions/getStats`, {
        params: { statsIds: idsToFetch }
      });
      setQuestionStats(prev => ({
        ...prev,
        ...res.data.stats
      }));
      idsToFetch.forEach(id => fetchedStatsRef.current.add(id));
      console.log("Stats fetched:", res.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const loadStats = async (ids = null) => {
    const finalIds = ids ?? Object.keys(confirmedAnswers);
    if (finalIds.length === 0) return;
    await fetchStats(finalIds);
  };

  const forceReloadStats = async (ids = null) => {
    const finalIds = ids ?? Object.keys(confirmedAnswers);
    if (finalIds.length === 0) return;
    await fetchStats(finalIds, true);
  };

  useEffect(() => {
    loadStats();
  }, [confirmedAnswers]);


  //------------------------------------------------
  // Verify and Save
  //------------------------------------------------

  // Handle Verification
  const handleVerify = async (questionId) => {

    const answer = answers[questionId];
    if (!answer.trim()) return; // Prevent verifying empty answers

    // Prevent redundant verification if the answer hasn't changed
    if (lastVerifiedAnswers[questionId] === answer.trim()) {
      console.log(`Answer for question ${questionId} has not changed, skipping verification.`);
      return;
    }

    setAnswerAwaitVerifying(prev => questionId);

    try {
      const res = await axiosInstance.post("/knovia/myInfo/verifyAnswer", {
        questionId,
        answer,
      });

      const { isValid, results } = res.data;

      console.log('isValid:', isValid);
      console.log('results:', results);

      // Expand verification statuses for all conditions
      const newVerificationStatus = [];

      const verifyRuleAI = infoQuestions.find(q => q._id === questionId)?.verifyRuleAI;

      if (verifyRuleAI) {
        verifyRuleAI.forEach((ruleSet, setIdx) => {
          ruleSet.conditions.forEach(() => {
            if (results[setIdx]?.decision === "YES") {
              newVerificationStatus.push("YES");
            } else {
              newVerificationStatus.push("NO");
            }
          });
        });
      }

      setRuleVerificationStatus((prev) => ({
        ...prev,
        [questionId]: newVerificationStatus,
      }));


      setLastVerifiedAnswers((prev) => ({
        ...prev,
        [questionId]: answer.trim(), // Store the verified answer
      }));

      if (isValid) {
        setVerifiedAnswers((prev) => ({
          ...prev,
          [questionId]: true,
        }));
        setConfirmedAnswers((prev) => ({
          ...prev,
          [questionId]: undefined, // Reset confirmed answers
        }));
        console.log(`Answer verified for question ${questionId}.`);
      } else {
        console.log(`Verification failed:`, results);
      }

      setAnswerAwaitVerifying(prev => "");
    } catch (error) {
      setAnswerAwaitVerifying(prev => "");
      console.error("Error verifying answer", error);
    }
  };


  // Handle Saving
  const handleSave = async (questionId) => {
    if (!verifiedAnswers[questionId]) return; // Prevent saving if not verified
    setAnswerAwaitSaving(prev => questionId);

    const answer = answers[questionId];

    try {
      const updateResult = await axiosInstance.post("/knovia/myInfo/updateAnswer", {
        questionId,
        answer,
      });

      const { message, hasAttributesAdded } = updateResult.data;

      console.log('hasAttributesAdded:', hasAttributesAdded);

      setConfirmedAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));

      // Fetch Question Array again
      if(hasAttributesAdded){
        console.log('fetching infoData...');
        await fetchInfoData();
        await loadSetAttributes();
      }

      setTimeout(() => {
        setAnswerAwaitSaving("");
      }, 1000);
      console.log(`Saved answer for question ${questionId}:`, answer);
    } catch (error) {
      setAnswerAwaitSaving(prev => "");
      console.error("Error saving answer", error);
    }
  };


  //------------------------------------------------
  // Render Logic
  //------------------------------------------------
  if (invalidTopic) {
    return (
      <div className="page-content">
        <Container fluid className="pt-5">
          <Card>
            <CardBody>

              <Row>
                <Col xs={12} className="d-flex align-items-center"
                  style={{ flexDirection: "column", margin: "3rem 0rem" }}
                >
                  <p style={{ fontSize: "24px", marginBottom: "2rem", color: "red" }}>這不是一個有效的主題</p>
                  <Button color="outline-primary" onClick={() => navigate("/myInfo/chooseTopic")}>
                    返回主題選擇
                  </Button>
                </Col>
              </Row>

            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  
  //------------------------------------------------
  // Return
  //------------------------------------------------
  return (
    <React.Fragment>

      <div className="page-content"
        style={{ 
          backgroundImage: `url(${townMap})`,
          /*backgroundSize: "contain"*/
        }}
      >

        <Container fluid>
          {/* Breadcrumb */}
          <BreadcrumbAboveImage
            title={t("My Info")}
            breadcrumbItems={[
              { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
              { title: t("測試主題填寫")},
            ]}
          />

          <Card>
            <CardBody>
              <div className="d-flex mb-3" 
                style={{ maxWidth: '60%', margin:"auto" }}
              >
                <AnimatedFlowText />
              </div>

              <div className="d-flex mb-3" 
                style={{ maxWidth: '60%', margin:"auto" }}
              >
                <AnimatedFlowTextCh />
              </div>

              <Row>
                <Col xs={6} className="d-flex justify-content-start align-items-center">
                  <div /*className="glow-underline mb-4"*/ className="mb-4" style={{ fontSize:'22px' }}>
                    <p>{t(`填寫主題：${infoTopic?.label}`)}</p>
                  </div>
                </Col>

                <Col xs={6} className="d-flex justify-content-end align-items-center">
                  <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                    onClick={(e) => { navigate(`/myInfo/chooseTopic`); }}
                  >
                    <i className='bx bx-arrow-back' ></i>{` 填寫其他主題`}
                  </Button>
                  <Button color="warning" style={{ marginRight: "1rem" }}
                    onClick={resetTopicProgress}
                  >
                    <p style={{ margin:'0' }}>全部重填</p>
                  </Button>
                </Col>
              </Row>

              <ConditionsList 
                showActivationConditions={showActivationConditions}
                setShowActivationConditions={setShowActivationConditions}
                showAffectedAttributes={showAffectedAttributes}
                setShowAffectedAttributes={setShowAffectedAttributes}
                questionSetAttributes={questionSetAttributes}
                setQuestionSetAttributes={setQuestionSetAttributes}
                activeTab={activeTab}
                fetchInfoData={fetchInfoData}
              />

              {/* Navigation Tabs */}
              <Nav tabs>
                {infoQuestionSets.map((set, index) => (
                  <NavItem key={set._id}>
                    <NavLink
                      className={classnames({ active: activeTab === set._id })}
                      onClick={() => toggleTab(set._id)}
                    >
                      {set.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab} 
                className="mainQAList"
                style={{ 
                  padding: "3rem 3rem 2rem 3rem",
                  border: "1px solid #c3c3c3",
                  borderRadius: "0px 0px 15px 15px",
                  borderTop: "none",
                }}
              >
                {isLoadingInfoData ? (
                  <InfoQuestionLoader />
                ) : animateMount ? (
                  infoQuestionSets.map((set) => (
                    <QuestionPane
                      key={set._id}
                      set={set}
                      infoQuestions={infoQuestions}

                      tooltipOpen={tooltipOpen}
                      toggleTooltip={toggleTooltip}
                      visibleStats={visibleStats}
                      animatingStats={animatingStats}
                      toggleStatsOpen={toggleStatsOpen}
                      questionStats={questionStats}
                      setQuestionStats={setQuestionStats}
                      loadStats={loadStats}
                      resolvedAnswerBuckets={resolvedAnswerBuckets}

                      showAffectedAttributes={showAffectedAttributes}
                      answers={answers}
                      answerAwaitSaving={answerAwaitSaving}
                      showActivationConditions={showActivationConditions}
                      verificationDetails={verificationDetails}
                      ruleVerificationStatus={ruleVerificationStatus}
                      verifiedAnswers={verifiedAnswers}
                      lastVerifiedAnswers={lastVerifiedAnswers}
                      answerAwaitVerifying={answerAwaitVerifying}
                      confirmedAnswers={confirmedAnswers}
                      
                      handleInputChange={handleInputChange}
                      handleVerify={handleVerify}
                      handleSave={handleSave}
                    />
                  ))
                ) : null}
              </TabContent>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

InfoInput.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(InfoInput);




